// Implements: F-SHR-003, F-SHR-009, F-UIX-007
/**
 * Links as the server lists them for ONE mockup (the share popover's list). Pure presentation.
 *
 * A row carries metadata only (the list has no field that could hold a URL), and it offers no
 * copy: the link is shown once, when it is created (owner question 3). The revoke action is
 * offered only where the server's per-link `may_revoke` says so (owner and org admin: every link;
 * an `editor`: the ones they created, owner decision 2026-10-07), and never on a link already
 * revoked. A link without the flag offers nothing: the client spells no role and guesses no
 * permission. A refusal still comes back as the server's sentence. The person's links across
 * mockups are the table of `LinksTable`.
 *
 * Mockup v3 P8: an expired link offers no "Revocar" (owner decision; it already opens nothing),
 * yet its label can still be renamed in place (`LinkLabel`, the same `may_revoke` rule, never on a
 * revoked link). A one-year link ("1 año") lists its real expiry date. An active link offers its eye,
 * "Ver como visitante" (`onVisitor`), which never counts a visit.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { dayText } from "../../../shared/ui/dayText";
import type { ShareLinkView } from "../../../types/sharing";
import { LinkLabel } from "./LinkLabel";
import styles from "./ShareDialog.module.css";

type StateKey =
  | "sharing.state.active"
  | "sharing.state.expired"
  | "sharing.state.revoked"
  | "sharing.state.suspended";

const STATES: Readonly<Partial<Record<string, StateKey>>> = {
  active: "sharing.state.active",
  expired: "sharing.state.expired",
  revoked: "sharing.state.revoked",
  suspended: "sharing.state.suspended",
};

export interface ShareLinkListProps<Link extends ShareLinkView> {
  readonly links: readonly Link[];
  /** The link whose revoke is in flight, if any. */
  readonly pendingId: string | null;
  readonly onRevoke: (link: Link) => void;
  /** Renames a link's label; answers the refusal's sentence, or `null` once it is accepted. */
  readonly onRename?: ((link: Link, label: string) => Promise<string | null>) | undefined;
  /** Opens what a visitor of this link sees (offered on active links only). */
  readonly onVisitor?: ((link: Link) => void) | undefined;
}

/** The name a row is read by: its label, or the dictionary's "no label". */
export function linkName(link: ShareLinkView, noLabel: string): string {
  return link.label === "" ? noLabel : link.label;
}

/** Whether the label and the expiry may change: the server's `may_revoke`, never on a revoked link. */
export function isEditable(link: ShareLinkView): boolean {
  return link.may_revoke === true && link.state !== "revoked";
}

/** Whether "Revocar" is offered: an editable link that has not expired (mockup v3 P8 owner decision). */
export function isRevocable(link: ShareLinkView): boolean {
  return isEditable(link) && link.state !== "expired";
}

export function ShareLinkList<Link extends ShareLinkView>({
  links,
  pendingId,
  onRevoke,
  onRename,
  onVisitor,
}: ShareLinkListProps<Link>): ReactElement {
  const { t } = useTranslation();
  if (links.length === 0) return <p className={styles.hint}>{t("sharing.list.empty")}</p>;

  return (
    <ul className={styles.list}>
      {links.map((link) => {
        const name = linkName(link, t("sharing.list.noLabel"));
        return (
          <li key={link.id} className={styles.row} aria-label={name}>
            <LinkLabel
              label={link.label}
              name={name}
              editable={isEditable(link)}
              onRename={onRename === undefined ? undefined : (label) => onRename(link, label)}
            />
            <span>
              {t(link.artifact === "diagram" ? "sharing.artifact.diagram" : "sharing.artifact.prototype")}
            </span>
            <span>{t(link.mode === "frozen" ? "sharing.mode.frozen" : "sharing.mode.live")}</span>
            <time dateTime={link.expires_at}>{t("sharing.list.expires", { day: dayText(link.expires_at) })}</time>
            <span>{t(STATES[link.state] ?? "sharing.state.unknown")}</span>
            {link.has_password ? <span>{t("sharing.panel.protected")}</span> : null}
            {onVisitor === undefined || link.state !== "active" ? null : (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                label={t("sharing.visitor.row", { label: name })}
                onClick={() => {
                  onVisitor(link);
                }}
              >
                <Icon name="eye" size="sm" />
              </Button>
            )}
            {!isRevocable(link) ? null : (
              <Button
                variant="danger"
                size="sm"
                label={t("sharing.revoke.for", { name })}
                disabled={pendingId !== null}
                loading={pendingId === link.id}
                onClick={() => {
                  onRevoke(link);
                }}
              >
                {t("sharing.revoke")}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
